<template>
  <!-- Tab Navigation -->
  <div class="flex justify-center bg-gray-800 border-b border-gray-700 sticky top-0 z-10">
    <button
      v-for="tab in tabs"
      :key="tab.id"
      @click="activeTab = tab.id"
      :class="[
        'px-4 py-2 text-sm font-medium transition-colors duration-200 relative',
        activeTab === tab.id
          ? 'text-indigo-300 border-b-2 border-indigo-600'
          : 'text-gray-300 hover:text-indigo-300 hover:bg-gray-700'
      ]"
    >
      {{ tab.name }}
      <span v-if="activeTab === tab.id" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"></span>
    </button>
  </div>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gray-900 text-white" id="style-1">
    <div class="p-4 max-w-7xl mx-auto">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center h-64 animate-fadeIn">
        <i class="fas fa-spinner text-4xl text-indigo-400 animate-spin-pulse"></i>
      </div>

      <!-- Buy Tab -->
      <div v-if="activeTab === 'buy'" class="space-y-4 animate-fadeIn">
        <!-- Weight, Filters, and Add Item Button -->
        <div class="flex flex-col sm:flex-row justify-between items-center bg-gray-900 bg-opacity-80 p-3 rounded-md border border-gray-800 space-y-3 sm:space-y-0">
          <div class="flex items-center space-x-2">
            <i class="fas fa-weight-hanging text-yellow-400 text-xs"></i>
            <span class="text-sm text-indigo-200">น้ำหนัก: <strong>{{ currentWeight || 0 }}</strong> / {{ maxLoad || 100 }}</span>
          </div>
          <div class="flex items-center space-x-3">
            <select
              v-model="currencyFilter"
              class="bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
            >
              <option value="all">ทั้งหมด</option>
              <option value="silver">เหรียญ</option>
              <option value="gold">ทอง</option>
              <option value="diamond">เพชร</option>
              <option value="cash">แคช</option>
            </select>
            <select
              v-model="typeFilter"
              class="bg-gray-700 bg-opacity-80 text-indigo-200 border border-gray-600 rounded-md p-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
            >
              <option value="all">ทุกประเภท</option>
              <option value="หมวก">หมวก</option>
              <option value="แว่นตา">แว่นตา</option>
              <option value="เสื้อ">เสื้อ</option>
              <option value="เสื้อคลุม">เสื้อคลุม</option>
              <option value="เกราะ">เกราะ</option>
              <option value="ปลอกแขน">ปลอกแขน</option>
              <option value="ถุงมือ">ถุงมือ</option>
              <option value="กางเกง">กางเกง</option>
              <option value="สนับแข้ง">สนับแข้ง</option>
              <option value="รองเท้า">รองเท้า</option>
              <option value="เข็มขัด">เข็มขัด</option>
              <option value="สร้อยคอ">สร้อยคอ</option>
              <option value="แหวน">แหวน</option>
              <option value="อาวุธ">อาวุธ</option>
              <option value="อาวุธสำรอง">อาวุธสำรอง</option>
              <option value="ตะกรุด">ตะกรุด</option>
              <option value="ผ้ายันต์">ผ้ายันต์</option>
              <option value="สายสิญจน์">สายสิญจน์</option>
              <option value="หนังสือ">หนังสือ</option>
              <option value="ทั่วไป">ทั่วไป</option>
              <option value="อาหาร">อาหาร</option>
              <option value="แผนที่">แผนที่</option>
              <option value="รักษา">รักษา</option>
              <option value="กัปดัก">กัปดัก</option>
              <option value="ปลดกัปดัก">ปลดกัปดัก</option>
              <option value="ระเบิด">ระเบิด</option>
              <option value="ปล้น">ปล้น</option>
            </select>
            <button
              v-if="isAdmin"
              @click="openAddItemModal"
              class="flex items-center bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-indigo-700 text-sm group"
            >
              <i class="fas fa-plus mr-1 text-sm group-hover:scale-110"></i> เพิ่มสินค้า
            </button>
          </div>
        </div>

        <!-- Alerts / Error -->
        <div v-if="warningMessage" class="bg-gray-900 bg-opacity-80 text-yellow-300 p-3 rounded-md text-center border border-yellow-600 text-sm">
          {{ warningMessage }}
        </div>
        <div v-if="error" class="bg-gray-900 bg-opacity-80 text-red-300 p-3 rounded-md text-center border border-red-600 text-sm">
          {{ error }}
        </div>

        <!-- Shop Items -->
        <div v-if="filteredShopItems.length > 0" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          <div
            v-for="item in filteredShopItems"
            :key="item.id"
            class="bg-gray-900 p-3 rounded-md shadow hover:bg-gray-800 bg-opacity-80 transition-all duration-200 cursor-pointer flex flex-col items-center border border-gray-800 relative"
            :class="{ 
              'border-2 border-indigo-600': selectedShopItem && selectedShopItem.id === item.id, 
              'border-2 border-yellow-600': item.isRecommended,
              'opacity-50 cursor-not-allowed': isExpired(item)
            }"
            @click="!isExpired(item) && selectShopItem(item)"
          >
            <div v-if="item.isRecommended" class="absolute top-2 left-2">
              <i class="fas fa-star text-yellow-400 text-xs"></i>
            </div>
            <div v-if="isAdmin" class="absolute top-2 right-2 flex space-x-2">
              <button
                @click.stop="openEditItemModal(item)"
                class="bg-indigo-600 hover:bg-indigo-500 text-white rounded-full w-6 h-6 flex items-center justify-center transition-all duration-200 border border-indigo-700 group"
              >
                <i class="fas fa-edit text-xs group-hover:scale-110"></i>
              </button>
              <button
                @click.stop="deleteShopItem(item.id)"
                class="bg-red-600 hover:bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center transition-all duration-200 border border-red-700 group"
              >
                <i class="fas fa-trash text-xs group-hover:scale-110"></i>
              </button>
            </div>
            <img
              :src="item.img || 'default-item.png'"
              alt="Item"
              class="w-16 h-16 rounded-md object-cover border-2 mb-2"
              :class="item.rarityColors?.borderColor || 'border-indigo-600'"
              @error="handleImageError"
            />
            <p
              class="font-semibold text-center text-sm truncate w-full"
              :class="item.rarityColors?.textColor || 'text-indigo-200'"
            >
              {{ item.name || 'ไม่ระบุ' }}
            </p>
            <p class="text-sm text-indigo-300 flex items-center">
              ราคา: {{ item.price?.toLocaleString() || 0 }}
              <i
                :class="{
                  'fas fa-coins text-gray-100': item.currencyType === 'silver',
                  'fas fa-g text-yellow-500': item.currencyType === 'gold',
                  'fas fa-gem text-blue-500': item.currencyType === 'diamond',
                  'fas fa-money-bill text-green-300': item.currencyType === 'cash',
                  'fas fa-question-circle text-gray-500': !item.currencyType,
                  'ml-1 text-xs': true
                }"
              ></i>
            </p>
            <p v-if="item.expirationDate" class="text-sm text-indigo-300 mt-1">
              หมดอายุ: {{ formatDate(item.expirationDate) }}
            </p>
            <p class="text-sm text-indigo-300 mt-1">
              <template v-if="getPurchaseInfo(item)?.max">
                {{ getPurchaseInfo(item).count }}/{{ getPurchaseInfo(item).max }} ครั้ง
              </template>
              <template v-else>
                ไม่จำกัดจำนวน
              </template>
            </p>
          </div>
        </div>
        <!-- No Items Case -->
        <div v-else class="bg-gray-900 bg-opacity-80 p-4 rounded-md text-center text-indigo-300 border border-gray-800 text-sm">
          <p>ไม่มีสินค้าในร้านสำหรับตัวกรองนี้</p>
        </div>
      </div>

      <!-- Expired Items Tab (Admin Only) -->
      <div v-else-if="activeTab === 'expired'" class="space-y-4 animate-fadeIn">
        <!-- Alerts / Error -->
        <div v-if="warningMessage" class="bg-gray-900 bg-opacity-80 text-yellow-300 p-3 rounded-md text-center border border-gray-700 text-sm">
          {{ warningMessage }}
        </div>
        <div v-if="error" class="bg-gray-900 bg-opacity-80 text-red-300 p-3 rounded-md text-center border border-red-600 text-sm">
          {{ error }}
        </div>
        <div v-if="isAdmin" class="flex flex-col sm:flex-row justify-between items-center gap-3 bg-gray-900 bg-opacity-80 p-3 rounded-md border border-gray-800">
          <p class="text-sm text-indigo-200">
            สินค้าหมดอายุที่พบ: <strong class="text-red-300">{{ expiredShopItems.length }}</strong> รายการ
          </p>
          <button
            @click="deleteExpiredShopItems"
            :disabled="isDeletingExpired || expiredShopItems.length === 0"
            class="flex items-center bg-red-700 hover:bg-red-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-red-800 text-sm group"
          >
            <span v-if="!isDeletingExpired">
              <i class="fas fa-trash mr-1 text-sm group-hover:scale-110"></i> ลบสินค้าหมดอายุทั้งหมด
            </span>
            <span v-else>
              <i class="fas fa-spinner mr-1 text-sm animate-spin-pulse"></i> กำลังลบ...
            </span>
          </button>
        </div>
        <!-- Expired Items -->
        <div v-if="expiredShopItems.length > 0" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          <div
            v-for="item in expiredShopItems"
            :key="item.id"
            class="bg-gray-900 bg-opacity-80 p-3 rounded-md shadow transition-all duration-200 flex flex-col items-center border border-gray-800 relative opacity-50"
          >
            <div v-if="item.isRecommended" class="absolute top-2 left-2">
              <i class="fas fa-star text-yellow-400 text-xs"></i>
            </div>
            <div v-if="isAdmin" class="absolute top-2 right-2 flex space-x-2">
              <button
                @click.stop="openEditItemModal(item)"
                class="bg-indigo-600 hover:bg-indigo-500 text-white rounded-full w-6 h-6 flex items-center justify-center transition-all duration-200 border border-indigo-700 group"
              >
                <i class="fas fa-edit text-xs group-hover:scale-110"></i>
              </button>
              <button
                @click.stop="deleteShopItem(item.id)"
                class="bg-red-600 hover:bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center transition-all duration-200 border border-red-700 group"
              >
                <i class="fas fa-trash text-xs group-hover:scale-110"></i>
              </button>
            </div>
            <img
              :src="item.img || 'default-item.png'"
              alt="Item"
              class="w-16 h-16 rounded-md object-cover border-2 mb-2"
              :class="item.rarityColors?.borderColor || 'border-indigo-600'"
              @error="handleImageError"
            />
            <p
              class="font-semibold text-center text-sm truncate w-full"
              :class="item.rarityColors?.textColor || 'text-indigo-200'"
            >
              {{ item.name || 'ไม่ระบุ' }}
            </p>
            <p class="text-sm text-indigo-300 flex items-center">
              ราคา: {{ item.price?.toLocaleString() || 0 }}
              <i
                :class="{
                  'fas fa-coins text-gray-100': item.currencyType === 'silver',
                  'fas fa-g text-yellow-500': item.currencyType === 'gold',
                  'fas fa-gem text-blue-500': item.currencyType === 'diamond',
                  'fas fa-money-bill text-green-500': item.currencyType === 'cash',
                  'fas fa-question-circle text-gray-500': !item.currencyType,
                  'ml-1 text-xs': true
                }"
              ></i>
            </p>
            <p class="text-sm text-indigo-300">สกุล: {{ currencyDisplay(item.currencyType) }}</p>
            <p v-if="item.expirationDate" class="text-sm text-red-300 mt-1">
              หมดอายุ: {{ formatDate(item.expirationDate) }}
            </p>
          </div>
        </div>
        <!-- No Expired Items Case -->
        <div v-else class="bg-gray-900 bg-opacity-80 p-4 rounded-md text-center text-indigo-300 border border-gray-800 text-sm">
          <p>ไม่มีสินค้าที่หมดอายุ</p>
        </div>
      </div>

      <!-- Navigation Back -->
      <footer class="mt-4">
        <router-link
          :to="{ name: 'Home' }"
          class="inline-flex items-center w-full bg-gray-900 bg-opacity-80 hover:bg-gray-800 text-indigo-200 p-3 justify-center rounded-md font-semibold transition-all duration-200 border border-gray-800 text-sm group"
        >
          <i class="fas fa-arrow-left mr-1 text-sm group-hover:scale-110"></i> กลับสู่เมนูหลัก
        </router-link>
      </footer>

      <!-- Modal for Buying Items -->
      <transition name="modal">
        <div v-if="showModal && activeTab === 'buy' && selectedShopItem" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 animate-modalFade">
          <div class="bg-gray-800 rounded-lg w-full max-w-md shadow-lg border border-gray-600 animate-scaleIn">
            <div class="p-4 border-b border-gray-700 flex justify-between items-center">
              <h1 class="text-lg font-semibold" :class="selectedShopItem.rarityColors?.textColor || 'text-indigo-300'">
                {{ selectedShopItem.name || 'ไม่ระบุ' }}
              </h1>
              <button @click="closeModal" class="text-gray-300 hover:text-indigo-300 group">
                <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <!-- Tabs -->
            <div class="flex border-b border-gray-700">
              <button
                @click="activeModalTab = 'info'"
                class="px-3 py-2 text-sm font-medium transition-colors duration-200 relative"
                :class="activeModalTab === 'info' ? 'text-indigo-300 border-b-2 border-indigo-600' : 'text-gray-300 hover:bg-gray-700 hover:text-indigo-300'"
              >
                ข้อมูลไอเทม
                <span v-if="activeModalTab === 'info'" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"></span>
              </button>
              <button
                v-if="selectedShopItem.decomposable"
                @click="activeModalTab = 'decompose'"
                class="px-3 py-2 text-sm font-medium transition-colors duration-200 relative"
                :class="activeModalTab === 'decompose' ? 'text-indigo-300 border-b-2 border-indigo-600' : 'text-gray-300 hover:bg-gray-700 hover:text-indigo-300'"
              >
                แยกชิ้นส่วน
                <span v-if="activeModalTab === 'decompose'" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"></span>
              </button>
            </div>
            <div class="p-4 space-y-4 max-h-[60vh] overflow-y-auto scrollbar-thin">
              <!-- Error Message -->
              <div v-if="modalError" class="text-red-300 text-sm text-center">
                {{ modalError }}
              </div>
              <!-- Tab: Item Info -->
              <div v-if="activeModalTab === 'info'" class="space-y-4">
                <div class="flex items-center gap-3">
                  <img
                    :src="selectedShopItem.img || 'default-item.png'"
                    alt="Item Image"
                    class="w-10 h-10 object-cover rounded-full border border-gray-600"
                    :class="selectedShopItem.rarityColors?.borderColor || 'border-indigo-600'"
                    @error="handleImageError"
                  />
                  <div>
                    <p class="text-indigo-300 text-xs">ประเภท: {{ selectedShopItem.type || 'ไม่ระบุ' }}</p>
                    <div v-if="selectedShopItem.rarity" class="flex items-center gap-1">
                      <template v-for="star in parseInt(selectedShopItem.rarity)" :key="star">
                        <i class="fas fa-star text-yellow-400 text-xs"></i>
                      </template>
                    </div>
                  </div>
                </div>
                <section class="bg-gray-700 rounded-md p-3 border border-gray-600">
                  <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลพื้นฐาน</h2>
                  <div class="grid grid-cols-1 gap-3 text-sm text-indigo-300">
                    <div v-if="selectedShopItem.type !== 'ทั่วไป'" class="flex items-center gap-2">
                      <i class="fas fa-user-shield text-gray-100 text-xs"></i>
                      <span>เลเวลที่ใช้ได้: <strong>{{ selectedShopItem.requiredLevel || 0 }}</strong></span>
                    </div>
                    <div class="flex items-center gap-2">
                      <i class="fas fa-weight-hanging text-gray-100 text-xs"></i>
                      <span>น้ำหนัก: <strong>{{ selectedShopItem.weight || 0 }}</strong></span>
                    </div>
                    <div class="flex items-center gap-2">
                      <i class="fas fa-tshirt text-gray-100 text-xs"></i>
                      <span>สวมใส่ได้: <strong>{{ selectedShopItem.wearable ? 'ใช่' : 'ไม่' }}</strong></span>
                    </div>
                    <div class="flex items-center gap-2">
                      <span>ราคา: <strong>{{ selectedShopItem.price?.toLocaleString() || 0 }}</strong>
                      <i
                        :class="{
                          'fas fa-coins text-gray-100': selectedShopItem?.currencyType === 'silver',
                          'fas fa-g text-yellow-500': selectedShopItem?.currencyType === 'gold',
                          'fas fa-gem text-blue-500': selectedShopItem?.currencyType === 'diamond',
                          'fas fa-money-bill text-green-500': selectedShopItem?.currencyType === 'cash',
                          'fas fa-question-circle text-gray-500': !selectedShopItem?.currencyType,
                          'ml-1 text-xs': true
                        }"
                      ></i>
                    </span>
                  </div>
                  </div>
                </section>
                <div v-if="selectedShopItem.expirationDate" class="flex items-center gap-2">
                  <i class="fas fa-calendar-times text-red-400 text-xs"></i>
                  <span>หมดอายุ: <strong>{{ formatDate(selectedShopItem.expirationDate) }}</strong></span>
                </div>
                <section v-if="selectedShopItem.description">
                  <h2 class="text-sm font-semibold text-indigo-200 mb-2">คำอธิบาย</h2>
                  <p class="text-indigo-300 text-xs border border-gray-600 p-3 rounded-md bg-gray-700">
                    {{ selectedShopItem.description }}
                  </p>
                </section>
                <section v-if="hasStats(selectedShopItem)">
                  <div v-if="selectedShopItem.stats?.stamina >= 1 || selectedShopItem.stats?.str >= 1 || selectedShopItem.stats?.agi >= 1 || selectedShopItem.stats?.int >= 1">
                    <h2 class="text-sm font-semibold text-indigo-200 mb-2">ค่าสถานะหลัก</h2>
                    <div class="grid grid-cols-2 gap-3 text-indigo-300 text-xs">
                      <div v-if="selectedShopItem.stats?.stamina >= 1" class="flex items-center gap-2">
                        <span>Stamina: <strong>{{ selectedShopItem.stats.stamina }}</strong></span>
                      </div>
                      <div v-if="selectedShopItem.stats?.str >= 1" class="flex items-center gap-2">
                        <span>Str: <strong>{{ selectedShopItem.stats.str }}</strong></span>
                      </div>
                      <div v-if="selectedShopItem.stats?.agi >= 1" class="flex items-center gap-2">
                        <span>Agi: <strong>{{ selectedShopItem.stats.agi }}</strong></span>
                      </div>
                      <div v-if="selectedShopItem.stats?.int >= 1" class="flex items-center gap-2">
                        <span>Int: <strong>{{ selectedShopItem.stats.int }}</strong></span>
                      </div>
                    </div>
                  </div>
                  <div v-if="selectedShopItem.stats?.maxHp >= 1 || selectedShopItem.stats?.atk >= 1 || selectedShopItem.stats?.evasion >= 1 || selectedShopItem.stats?.critical >= 1 || selectedShopItem.stats?.defense >= 1">
                    <h2 class="text-sm font-semibold text-indigo-200 mb-2">ค่าสถานะเสริม</h2>
                    <div class="grid grid-cols-2 gap-3 text-indigo-300 text-xs">
                      <div v-if="selectedShopItem.stats?.maxHp >= 1" class="flex items-center gap-2">
                        <span>Max HP: <strong>{{ selectedShopItem.stats.maxHp }}</strong></span>
                      </div>
                      <div v-if="selectedShopItem.stats?.atk >= 1" class="flex items-center gap-2">
                        <span>Atk: <strong>{{ selectedShopItem.stats.atk }}</strong></span>
                      </div>
                      <div v-if="selectedShopItem.stats?.evasion >= 1" class="flex items-center gap-2">
                        <span>Evasion: <strong>{{ selectedShopItem.stats.evasion }}</strong></span>
                      </div>
                      <div v-if="selectedShopItem.stats?.critical >= 1" class="flex items-center gap-2">
                        <span>Critical: <strong>{{ selectedShopItem.stats.critical }}</strong></span>
                      </div>
                      <div v-if="selectedShopItem.stats?.defense >= 1" class="flex items-center gap-2">
                        <span>Defense: <strong>{{ selectedShopItem.stats.defense }}</strong></span>
                      </div>
                    </div>
                  </div>
                </section>
                <div class="flex items-center gap-2">
                  <label class="text-sm text-indigo-200">จำนวน:</label>
                  <div class="flex items-center bg-gray-700 border border-gray-600 rounded-md overflow-hidden">
                    <button
                      @click="decreaseQuantity"
                      :disabled="purchaseQuantity <= 1"
                      class="px-3 py-1.5 text-indigo-200 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <i class="fas fa-minus text-xs"></i>
                    </button>
                    <span class="px-4 py-1.5 text-indigo-200 font-semibold text-sm border-x border-gray-600">
                      {{ purchaseQuantity }}
                    </span>
                    <button
                      @click="increaseQuantity"
                      :disabled="purchaseQuantity >= maxPurchaseQuantity"
                      class="px-3 py-1.5 text-indigo-200 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <i class="fas fa-plus text-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
              <!-- Tab: Decompose -->
              <div v-if="activeModalTab === 'decompose'" class="space-y-3">
                <div v-if="decomposeLoading" class="flex items-center justify-center py-4">
                  <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-indigo-600"></div>
                </div>
                <div v-else-if="decomposeItems.length === 0" class="text-indigo-300 text-sm text-center">
                  <p>ไม่มีข้อมูลการแยกชิ้นส่วนสำหรับไอเทมนี้</p>
                </div>
                <div v-else class="space-y-2 max-h-40 overflow-y-auto scrollbar-thin">
                  <div
                    v-for="decomposeItem in decomposeItems"
                    :key="decomposeItem.itemName"
                    class="flex items-center gap-3 p-3 bg-gray-700" 
                  >
                    <img
                      :src="decomposeItem.img || 'default-item.png'"
                      alt="Decompose Item Image"
                      class="w-10 h-10 object-cover rounded-full border border-gray-600"
                      :class="decomposeItem.rarityColors?.borderColor || 'border-indigo-600'"
                      @error="handleImageError"
                    />
                    <div>
                      <h3 class="text-sm font-semibold text-indigo-200">{{ decomposeItem.name }}</h3>
                      <p class="text-indigo-300 text-xs">โอกาส: <strong>{{ decomposeItem.chance.toFixed(2) }}%</strong></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="p-4 border-t border-gray-700 flex justify-end gap-4">
              <button
                @click="buyItem"
                class="flex items-center bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-indigo-700 text-sm group"
                :class="{ 'opacity-60 cursor-not-allowed': isBuying || isExpired(selectedShopItem) }"
                :disabled="isBuying || isExpired(selectedShopItem)"
              >
                <span v-if="!isBuying"><i class="fas fa-coins mr-1 text-sm group-hover:scale-110"></i> ซื้อ</span>
                <i v-else class="fas fa-spinner animate-spin-pulse text-sm"></i>
              </button>
              <button
                @click="closeModal"
                class="flex items-center bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm group"
              >
                <i class="fas fa-times mr-1 text-sm group-hover:scale-110"></i> ปิด
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Modal for Adding Item -->
      <transition name="modal">
        <div v-if="showAddItemModal" class="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-70 z-50 animate-modalFade">
          <div class="bg-gray-800 rounded-lg w-full max-w-md p-6 shadow-lg border border-gray-600 animate-scaleIn">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-semibold text-indigo-300">เพิ่มสินค้า</h3>
              <button @click="closeAddItemModal" class="text-gray-300 hover:text-indigo-300 group">
                <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <div class="space-y-4">
              <div>
                <label class="block text-indigo-200 mb-1 text-sm">ชื่อไอเทม</label>
                <input
                  v-model="newItemName"
                  type="text"
                  class="w-full bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  placeholder="เช่น ดาบเหล็ก"
                />
              </div>
              <div>
                <label class="block text-indigo-200 mb-1 text-sm">ประเภทไอเทม</label>
                <select
                  v-model="newItemType"
                  class="w-full bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                >
                  <option value="" disabled="true">กรุณาเลือกประเภทไอเทม</option>
                  <option value="หมวก">หมวก</option>
                  <option value="แว่นตา">แว่นตา</option>
                  <option value="เสื้อ">เสื้อ</option>
                  <option value="เสื้อคลุม">เสื้อคลุม</option>
                  <option value="เกราะ">เกราะ</option>
                  <option value="ปลอกแขน">ปลอกแขน</option>
                  <option value="ถุงมือ">ถุงมือ</option>
                  <option value="กางเกง">กางเกง</option>
                  <option value="สนับแข้ง">สนับแข้ง</option>
                  <option value="รองเท้า">รองเท้า</option>
                  <option value="เข็มขัด">เข็มขัด</option>
                  <option value="สร้อยคอ">สร้อยคอ</option>
                  <option value="แหวน">แหวน</option>
                  <option value="อาวุธ">อาวุธ</option>
                  <option value="อาวุธสำรอง">อาวุธสำรอง</option>
                  <option value="ตะกรุด">ตะกรุด</option>
                  <option value="ผ้ายันต์">ผ้ายันต์</option>
                  <option value="สายสิญจน์">สายสิญจน์</option>
                  <option value="หนังสือ">หนังสือ</option>
                  <option value="ทั่วไป">ทั่วไป</option>
                  <option value="อาหาร">อาหาร</option>
                  <option value="แผนที่">แผนที่</option>
                  <option value="รักษา">รักษา</option>
                  <option value="กัปดัก">กัปดัก</option>
                  <option value="ปลดกัปดัก">ปลดกัปดัก</option>
              <option value="ระเบิด">ระเบิด</option>
                  <option value="ปล้น">ปล้น</option>
                </select>
              </div>
              <div>
                <label class="block text-indigo-200 mb-1 text-sm">ราคา</label>
                <input
                  v-model.number="newItemPrice"
                  type="number"
                  min="0"
                  class="w-full bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  placeholder="เช่น 100"
                />
              </div>
              <div>
                <label class="block text-indigo-200 mb-1 text-sm">ประเภทเงินที่ชำระ</label>
                <select
                  v-model="newItemCurrency"
                  class="w-full bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                >
                  <option value="" disabled="true">กรุณาเลือกประเภทเงิน</option>
                  <option value="silver">Silver</option>
                  <option value="gold">Gold</option>
                  <option value="diamond">Diamond</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
              <div>
                <label class="block text-indigo-200 mb-1 text-sm">ลำดับการแสดง (เลขยิ่งน้อยยิ่งแสดงก่อน)</label>
                <input
                  v-model.number="newItemSortOrder"
                  type="number"
                  min="0"
                  class="w-full bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  placeholder="เช่น 1"
                />
              </div>
              <div>
                <label class="block text-indigo-200 mb-1 text-sm">จำนวนครั้งที่ซื้อได้สูงสุด (ว่างไว้หากไม่จำกัด)</label>
                <input
                  v-model.number="newItemMaxPurchases"
                  type="number"
                  min="0"
                  class="w-full bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  placeholder="ว่างไว้หากไม่จำกัด"
                />
              </div>
              <div>
                <label class="block text-indigo-200 mb-1 text-sm">วันและเวลาหมดอายุ (ว่างไว้หากไม่มี)</label>
                <input
                  v-model="newItemExpirationDate"
                  type="datetime-local"
                  class="w-full bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                />
              </div>
              <div class="flex items-center">
                <input
                  v-model="newItemIsRecommended"
                  type="checkbox"
                  class="bg-gray-700 rounded text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600 border border-gray-600"
                />
                <label class="ml-2 text-indigo-200 text-sm">ตั้งเป็นไอเทมแนะนำ</label>
              </div>
            </div>
            <div class="flex justify-end gap-4 mt-6">
              <button
                @click="addItemToShop"
                class="flex items-center bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-indigo-700 text-sm group"
                :class="{ 'opacity-60 cursor-not-allowed': isAdding }"
                :disabled="isAdding"
              >
                <span v-if="!isAdding"><i class="fas fa-plus mr-1 text-sm group-hover:scale-110"></i> เพิ่ม</span>
                <i v-else class="fas fa-spinner animate-spin-pulse text-sm"></i>
              </button>
              <button
                @click="closeAddItemModal"
                class="flex items-center bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm group"
              >
                <i class="fas fa-times mr-1 text-sm group-hover:scale-110"></i> ยกเลิก
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Modal for Editing Item -->
      <transition name="modal">
        <div v-if="showEditItemModal" class="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-70 z-50 animate-modalFade">
          <div class="bg-gray-800 rounded-lg w-full max-w-md p-6 shadow-lg border border-gray-600 animate-scaleIn">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-semibold text-indigo-300">แก้ไขสินค้า: {{ editItemName || 'ไม่ระบุ' }}</h3>
              <button @click="closeEditItemModal" class="text-gray-300 hover:text-indigo-300 group">
                <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <div class="space-y-4">
              <div>
                <label class="block text-indigo-200 mb-1 text-sm">ประเภทไอเทม</label>
                <select
                  v-model="editItemType"
                  class="w-full bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                >
                  <option value="" disabled>กรุณาเลือกประเภทไอเทม</option>
                  <option value="หมวก">หมวก</option>
                  <option value="แว่นตา">แว่นตา</option>
                  <option value="เสื้อ">เสื้อ</option>
                  <option value="เสื้อคลุม">เสื้อคลุม</option>
                  <option value="เกราะ">เกราะ</option>
                  <option value="ปลอกแขน">ปลอกแขน</option>
                  <option value="ถุงมือ">ถุงมือ</option>
                  <option value="กางเกง">กางเกง</option>
                  <option value="สนับแข้ง">สนับแข้ง</option>
                  <option value="รองเท้า">รองเท้า</option>
                  <option value="เข็มขัด">เข็มขัด</option>
                  <option value="สร้อยคอ">สร้อยคอ</option>
                  <option value="แหวน">แหวน</option>
                  <option value="อาวุธ">อาวุธ</option>
                  <option value="อาวุธสำรอง">อาวุธสำรอง</option>
                  <option value="ตะกรุด">ตะกรุด</option>
                  <option value="ผ้ายันต์">ผ้ายันต์</option>
                  <option value="สายสิญจน์">สายสิญจน์</option>
                  <option value="หนังสือ">หนังสือ</option>
                  <option value="ทั่วไป">ทั่วไป</option>
                  <option value="อาหาร">อาหาร</option>
                  <option value="แผนที่">แผนที่</option>
                  <option value="รักษา">รักษา</option>
                  <option value="กัปดัก">กัปดัก</option>
                  <option value="ปลดกัปดัก">ปลดกัปดัก</option>
              <option value="ระเบิด">ระเบิด</option>
                  <option value="ปล้น">ปล้น</option>
                </select>
              </div>
              <div>
                <label class="block text-indigo-200 mb-1 text-sm">ราคา</label>
                <input
                  v-model.number="editItemPrice"
                  type="number"
                  min="0"
                  class="w-full bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  placeholder="เช่น 100"
                />
              </div>
              <div>
                <label class="block text-indigo-200 mb-1 text-sm">ประเภทเงินที่ชำระ</label>
                <select
                  v-model="editItemCurrency"
                  class="w-full bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                >
                  <option value="" disabled>กรุณาเลือกประเภทเงิน</option>
                  <option value="silver">Silver</option>
                  <option value="gold">Gold</option>
                  <option value="diamond">Diamond</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
              <div>
                <label class="block text-indigo-200 mb-1 text-sm">ลำดับการแสดง (เลขยิ่งน้อยยิ่งแสดงก่อน)</label>
                <input
                  v-model.number="editItemSortOrder"
                  type="number"
                  min="0"
                  class="w-full bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  placeholder="เช่น 0"
                />
              </div>
              <div>
                <label class="block text-indigo-200 mb-1 text-sm">จำนวนครั้งที่ซื้อได้สูงสุด (ว่างไว้หากไม่จำกัด)</label>
                <input
                  v-model.number="editItemMaxPurchases"
                  type="number"
                  min="0"
                  class="w-full bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  placeholder="ว่างไว้หากไม่จำกัด"
                />
              </div>
              <div>
                <label class="block text-indigo-200 mb-1 text-sm">วันและเวลาหมดอายุ (ว่างไว้หากไม่มี)</label>
                <input
                  v-model="editItemExpirationDate"
                  type="datetime-local"
                  class="w-full bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                />
              </div>
              <div class="flex items-center">
                <input
                  v-model="editItemIsRecommended"
                  type="checkbox"
                  class="bg-gray-700 rounded text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600 border border-gray-600"
                />
                <label class="ml-2 text-indigo-200 text-sm">ตั้งเป็นไอเทมแนะนำ</label>
              </div>
            </div>
            <div class="flex justify-end gap-4 mt-6">
              <button
                @click="updateShopItem"
                class="flex items-center bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-indigo-700 text-sm group"
                :class="{ 'opacity-60 cursor-not-allowed': isUpdating }"
                :disabled="isUpdating"
              >
                <span v-if="!isUpdating"><i class="fas fa-save mr-1 text-sm group-hover:scale-110"></i> บันทึก</span>
                <i v-else class="fas fa-spinner animate-spin-pulse text-sm"></i>
              </button>
              <button
                @click="closeEditItemModal"
                class="flex items-center bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm group"
              >
                <i class="fas fa-times mr-1 text-sm group-hover:scale-110"></i> ยกเลิก
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import dayjs from 'dayjs';
import gameItems from '@/data/gameItems.json';

export default {
  name: 'Shop',
  setup() {
    const { user } = getUser();
    const playerStore = usePlayerStore();

    const isAdmin = ref(false);
    const activeTab = ref('buy');
    const activeModalTab = ref('info');

    const shopItems = ref([]);
    const expiredShopItems = ref([]);

    const silver = ref(0);
    const currentWeight = ref(0);
    const maxLoad = ref(100);

    const loading = ref(true);
    const error = ref(null);
    const modalError = ref('');
    const warningMessage = ref('');

    const showModal = ref(false);
    const showAddItemModal = ref(false);
    const showEditItemModal = ref(false);

    const selectedShopItem = ref(null);

    const isBuying = ref(false);
    const isAdding = ref(false);
    const isUpdating = ref(false);
    const isDeletingExpired = ref(false);

    const newItemName = ref('');
    const newItemType = ref('');
    const newItemPrice = ref(0);
    const newItemCurrency = ref('');
    const newItemMaxPurchases = ref(null);
    const newItemIsRecommended = ref(false);
    const newItemExpirationDate = ref('');
    const newItemSortOrder = ref(0);

    const editItemId = ref('');
    const editItemName = ref('');
    const editItemType = ref('');
    const editItemPrice = ref(0);
    const editItemCurrency = ref('');
    const editItemMaxPurchases = ref(null);
    const editItemIsRecommended = ref(false);
    const editItemExpirationDate = ref('');
    const editItemSortOrder = ref(0);

    const currencyFilter = ref('all');
    const typeFilter = ref('all');

    const userShopData = ref({});
    const decomposeItems = ref([]);
    const decomposeLoading = ref(false);
    const purchaseQuantity = ref(1);

    let unsubscribeShop = [];
    let unsubscribePurchases = [];

    watch(
      () => playerStore.characterData,
      (data) => {
        silver.value = data?.silver || 0;
      },
      { immediate: true }
    );

    watch(
      () => playerStore.userData,
      (data) => {
        isAdmin.value = data?.admin === true;
      },
      { immediate: true }
    );

    const tabs = computed(() => {
      const baseTabs = [
        { id: 'buy', name: 'ซื้อ' },
      ];

      if (isAdmin.value) {
        baseTabs.push({ id: 'expired', name: 'สินค้าหมดอายุ' });
      }

      return baseTabs;
    });

    const formatDate = (dateString) => {
      if (!dateString) return 'ไม่มีกำหนด';

      const date = dayjs(dateString);

      if (!date.isValid()) return 'วันที่ไม่ถูกต้อง';

      return date.format('DD/MM/YYYY HH:mm');
    };

    const currencyDisplay = (currencyType) => {
      const currencyMap = {
        silver: 'เหรียญ',
        gold: 'ทอง',
        diamond: 'เพชร',
        cash: 'แคช',
      };

      return currencyMap[currencyType] || 'ไม่ระบุ';
    };

    const isExpired = (item) => {
      if (!item?.expirationDate) return false;

      const expiration = dayjs(item.expirationDate);
      const now = dayjs();

      return now.isAfter(expiration);
    };

    const getPurchaseInfo = (item) => {
      if (!item?.id) {
        console.warn('getPurchaseInfo: No item ID provided', item);
        return { count: 0, max: null };
      }

      const shopItemId = item.id;

      return userShopData.value[shopItemId] || {
        count: 0,
        max: item.maxPurchases || null,
      };
    };

    const getRemainingPurchases = (item) => {
      const info = getPurchaseInfo(item);

      if (info.max === null) return Infinity;

      return info.max - info.count;
    };

    const maxPurchaseQuantity = computed(() => {
      if (!selectedShopItem.value) return 10;

      const remaining = getRemainingPurchases(selectedShopItem.value);

      return Math.min(10, remaining);
    });

    const hasStats = (item) => {
      if (!item?.stats) return false;

      const s = item.stats;

      return (
        (s.stamina || 0) >= 1 ||
        (s.str || 0) >= 1 ||
        (s.agi || 0) >= 1 ||
        (s.int || 0) >= 1 ||
        (s.maxHp || 0) >= 1 ||
        (s.atk || 0) >= 1 ||
        (s.defense || 0) >= 1 ||
        (s.evasion || 0) >= 1 ||
        (s.critical || 0) >= 1
      );
    };

    const handleImageError = (event) => {
      event.target.src = '';
    };

    const fetchDecomposeItems = () => {
      if (
        !selectedShopItem.value?.decomposable ||
        !Array.isArray(selectedShopItem.value.decomposeList) ||
        selectedShopItem.value.decomposeList.length === 0
      ) {
        decomposeItems.value = [];
        return;
      }

      decomposeLoading.value = true;
      modalError.value = '';

      try {
        const items = [];

        for (const decompose of selectedShopItem.value.decomposeList) {
          if (decompose?.itemName) {
            const itemData = gameItems[decompose.itemName] || {};

            items.push({
              itemName: decompose.itemName,
              name: itemData.name || decompose.itemName,
              img: itemData.img || 'default-item.png',
              chance: decompose.chance || 0,
              rarityColors: itemData.rarityColors || {
                borderColor: 'border-indigo-600',
              },
            });
          }
        }

        decomposeItems.value = items.sort((a, b) => b.chance - a.chance);
      } catch (err) {
        console.error('Error fetching decompose items:', err);
        modalError.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลการแยกชิ้นส่วน';
        decomposeItems.value = [];
      } finally {
        decomposeLoading.value = false;
      }
    };

    const filteredShopItems = computed(() => {
      const currencyOrder = {
        silver: 1,
        gold: 2,
        diamond: 3,
        cash: 4,
      };

      return shopItems.value
        .filter((item) => {
          if (isExpired(item)) return false;
          if (currencyFilter.value !== 'all' && item.currencyType !== currencyFilter.value) return false;
          if (typeFilter.value !== 'all' && item.type !== typeFilter.value) return false;

          return true;
        })
        .sort((a, b) => {
          if (a.isRecommended && !b.isRecommended) return -1;
          if (!a.isRecommended && b.isRecommended) return 1;

          const sortOrderA = a.sortOrder || 0;
          const sortOrderB = b.sortOrder || 0;

          if (sortOrderA !== sortOrderB) return sortOrderA - sortOrderB;

          const currencyA = currencyOrder[a.currencyType] || 5;
          const currencyB = currencyOrder[b.currencyType] || 5;

          if (currencyA !== currencyB) return currencyA - currencyB;

          return (a.price || 0) - (b.price || 0);
        });
    });

    const clearPurchaseListeners = () => {
      unsubscribePurchases.forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });

      unsubscribePurchases = [];
    };

    const fetchUserShopData = (visibleShopItemIds = shopItems.value.map((item) => item.id)) => {
      if (!user.value) {
        console.warn('No user logged in, skipping fetchUserShopData');
        return;
      }

      const userId = user.value.uid;
      const uniqueItemIds = [...new Set(visibleShopItemIds.filter(Boolean))];

      clearPurchaseListeners();
      userShopData.value = {};

      console.log('[Shop] shopPurchases loaded docs:', uniqueItemIds.length);

      if (uniqueItemIds.length === 0) {
        return;
      }

      const purchaseDataByItemId = {};

      const updatePurchaseData = () => {
        userShopData.value = { ...purchaseDataByItemId };
      };

      try {
        uniqueItemIds.forEach((shopItemId) => {
          const docId = `${userId}_${shopItemId}`;

          const unsubscribe = projectFirestore
            .collection('shopPurchases')
            .doc(docId)
            .onSnapshot(
              (doc) => {
                if (doc.exists) {
                  const data = doc.data();

                  purchaseDataByItemId[shopItemId] = {
                    count: data.count || 0,
                    max: data.max || null,
                  };
                } else {
                  delete purchaseDataByItemId[shopItemId];
                }

                updatePurchaseData();
              },
              (err) => {
                console.error('Error fetching direct shop purchase doc:', err);
                error.value = 'เกิดข้อผิดพลาดในการดึงข้อมูลการซื้อ';
              }
            );

          unsubscribePurchases.push(unsubscribe);
        });
      } catch (err) {
        console.error('Error setting up shop purchases listener:', err);
        error.value = 'เกิดข้อผิดพลาดในการเชื่อมต่อฐานข้อมูล';
      }
    };

    const clearShopListeners = () => {
      unsubscribeShop.forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });

      unsubscribeShop = [];
    };

    const buildShopItem = (doc) => {
      try {
        const shopItem = doc.data();

        if (!shopItem.currencyType || !shopItem.name) {
          console.warn(`Skipping invalid shop item ${doc.id}: missing currencyType or name`, shopItem);
          return null;
        }

        const itemData = gameItems[shopItem.name];

        if (!itemData) {
          console.warn(`Item ${shopItem.name} not found in gameItems.json for shop item ${doc.id}`);
          return null;
        }

        return {
          id: doc.id,
          name: shopItem.name,
          type: shopItem.type || itemData.type || 'ทั่วไป',
          price: shopItem.price || 0,
          currencyType: shopItem.currencyType || 'silver',
          maxPurchases: shopItem.maxPurchases || null,
          isRecommended: shopItem.isRecommended || false,
          expirationDate: shopItem.expirationDate || null,
          sortOrder: shopItem.sortOrder || 0,
          img: itemData.img || 'default-item.png',
          rarity: itemData.rarity || null,
          rarityColors: itemData.rarityColors || {
            borderColor: 'border-indigo-600',
            textColor: 'text-indigo-200',
          },
          requiredLevel: itemData.requiredLevel || 0,
          weight: itemData.weight || 0,
          wearable: itemData.wearable || false,
          description: itemData.description || '',
          stats: itemData.stats || {},
          decomposable: itemData.decomposable || false,
          decomposeList: itemData.decomposeList || [],
        };
      } catch (err) {
        console.error(`Error processing shop item ${doc.id}:`, err);
        return null;
      }
    };

    const applyShopFilters = (query) => {
      let filteredQuery = query;

      if (currencyFilter.value !== 'all') {
        filteredQuery = filteredQuery.where('currencyType', '==', currencyFilter.value);
      }

      if (typeFilter.value !== 'all') {
        filteredQuery = filteredQuery.where('type', '==', typeFilter.value);
      }

      return filteredQuery;
    };

    const listenToPublicShopItems = async () => {
      clearShopListeners();
      shopItems.value = [];
      expiredShopItems.value = [];

      const nowIso = new Date().toISOString();
      const activeItemsById = new Map();

      const mergeItems = (snapshot) => {
        snapshot.docs.forEach((doc) => {
          const item = buildShopItem(doc);

          if (item) {
            activeItemsById.set(doc.id, item);
          }
        });
      };

      const noExpirationQuery = projectFirestore
        .collection('gameShopItems')
        .where('expirationDate', '==', null);

      const futureExpirationQuery = projectFirestore
        .collection('gameShopItems')
        .where('expirationDate', '>', nowIso);

      try {
        const [noExpirationSnapshot, futureExpirationSnapshot] = await Promise.all([
          noExpirationQuery.get(),
          futureExpirationQuery.get(),
        ]);

        console.log(
          '[Shop] gameShopItems loaded docs:',
          noExpirationSnapshot.size + futureExpirationSnapshot.size
        );

        mergeItems(noExpirationSnapshot);
        mergeItems(futureExpirationSnapshot);

        shopItems.value = Array.from(activeItemsById.values());

        fetchUserShopData(shopItems.value.map((item) => item.id));

        loading.value = false;
        error.value = shopItems.value.length === 0
          ? 'ไม่มีสินค้าในร้านค้า'
          : null;
      } catch (err) {
        console.error('Error fetching active shop items:', err);
        error.value = 'เกิดข้อผิดพลาดในการดึงข้อมูลร้านค้า';
        loading.value = false;
      }
    };

    const listenToExpiredShopItems = async () => {
      clearShopListeners();
      clearPurchaseListeners();

      shopItems.value = [];
      expiredShopItems.value = [];
      userShopData.value = {};

      if (!isAdmin.value) {
        activeTab.value = 'buy';
        loading.value = false;
        return;
      }

      const nowIso = new Date().toISOString();

      const expiredQuery = projectFirestore
        .collection('gameShopItems')
        .where('expirationDate', '<', nowIso);

      try {
        const snapshot = await expiredQuery.get();

        console.log('[Shop] gameShopItems loaded docs:', snapshot.size);
        console.log('[Shop] shopPurchases loaded docs:', 0);

        expiredShopItems.value = snapshot.docs
          .map((doc) => buildShopItem(doc))
          .filter((item) => item !== null)
          .sort((a, b) => dayjs(b.expirationDate || 0).diff(dayjs(a.expirationDate || 0)));

        loading.value = false;
        error.value = null;
      } catch (err) {
        console.error('Error fetching expired shop items:', err);
        error.value = 'เกิดข้อผิดพลาดในการดึงสินค้าหมดอายุ';
        loading.value = false;
      }
    };

    const fetchShopItems = async () => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอินเพื่อเข้าถึงร้านค้า';
        loading.value = false;
        return;
      }

      warningMessage.value = '';

      if (activeTab.value === 'expired') {
        await listenToExpiredShopItems();
        return;
      }

      await listenToPublicShopItems();
    };

    watch(activeTab, () => {
      loading.value = true;
      fetchShopItems();
    });

    watch(isAdmin, (admin) => {
      if (!admin && activeTab.value === 'expired') {
        activeTab.value = 'buy';
      }
    });

    watch(activeModalTab, (newTab) => {
      if (newTab === 'decompose' && selectedShopItem.value) {
        fetchDecomposeItems();
      }
    });

    watch(selectedShopItem, () => {
      decomposeItems.value = [];
      modalError.value = '';
      purchaseQuantity.value = 1;

      if (selectedShopItem.value && activeModalTab.value === 'decompose') {
        fetchDecomposeItems();
      }
    });

    const selectShopItem = (item) => {
      if (!item?.id || !item.currencyType) {
        console.error('Invalid item selected:', item);
        error.value = 'ไม่สามารถเลือกไอเทมที่ไม่ถูกต้องได้';
        return;
      }

      selectedShopItem.value = item;
      showModal.value = true;
      activeModalTab.value = 'info';
      warningMessage.value = '';
      error.value = '';
      modalError.value = '';
    };

    const buyItem = async () => {
      if (!user.value || !selectedShopItem.value?.id) {
        error.value = 'กรุณาเลือกไอเทมเพื่อซื้อ';
        return;
      }

      if (purchaseQuantity.value < 1 || purchaseQuantity.value > maxPurchaseQuantity.value) {
        modalError.value = 'จำนวนไม่ถูกต้อง';
        return;
      }

      isBuying.value = true;
      error.value = '';
      modalError.value = '';

      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');

        await gameActions({
          action: 'buyItem',
          shopItemId: selectedShopItem.value.id,
          quantity: purchaseQuantity.value,
        });

        showModal.value = false;
        selectedShopItem.value = null;
        warningMessage.value = 'ซื้อไอเทมสำเร็จ!';
      } catch (err) {
        console.error('Error buying item:', err);
        error.value = err.message || 'เกิดข้อผิดพลาดในการซื้อไอเทม';
        modalError.value = err.message || 'เกิดข้อผิดพลาดในการซื้อ';
      } finally {
        isBuying.value = false;
      }
    };

    const deleteShopItem = async (itemId) => {
      if (!user.value || !isAdmin.value || !itemId) {
        error.value = 'คุณไม่มีสิทธิ์ลบสินค้าหรือไอดีไม่ถูกต้อง';
        return;
      }

      try {
        await projectFirestore.collection('gameShopItems').doc(itemId).delete();
        warningMessage.value = 'ลบสินค้าสำเร็จ!';

        await fetchShopItems();
      } catch (err) {
        console.error('Error deleting shop item:', err);
        error.value = 'เกิดข้อผิดพลาดในการลบสินค้า';
      }
    };

    const deleteExpiredShopItems = async () => {
      if (!user.value || !isAdmin.value) {
        error.value = 'คุณไม่มีสิทธิ์ลบสินค้าหมดอายุ';
        return;
      }

      if (expiredShopItems.value.length === 0) return;

      const confirmed = window.confirm(`ต้องการลบสินค้าหมดอายุที่แสดงอยู่ ${expiredShopItems.value.length} รายการหรือไม่?`);

      if (!confirmed) return;

      isDeletingExpired.value = true;
      error.value = '';
      warningMessage.value = '';

      try {
        const nowIso = new Date().toISOString();
        let deletedCount = 0;

        while (true) {
          const expiredQuery = applyShopFilters(
            projectFirestore
              .collection('gameShopItems')
              .where('expirationDate', '<', nowIso)
          ).limit(500);

          const snapshot = await expiredQuery.get();

          if (snapshot.empty) break;

          const batch = projectFirestore.batch();

          snapshot.docs.forEach((doc) => {
            batch.delete(doc.ref);
          });

          await batch.commit();

          deletedCount += snapshot.size;

          if (snapshot.size < 500) break;
        }

        warningMessage.value = `ลบสินค้าหมดอายุสำเร็จ ${deletedCount} รายการ`;

        await fetchShopItems();
      } catch (err) {
        console.error('Error deleting expired shop items:', err);
        error.value = 'เกิดข้อผิดพลาดในการลบสินค้าหมดอายุ';
      } finally {
        isDeletingExpired.value = false;
      }
    };

    const openAddItemModal = () => {
      newItemName.value = '';
      newItemType.value = '';
      newItemPrice.value = 0;
      newItemCurrency.value = '';
      newItemMaxPurchases.value = null;
      newItemIsRecommended.value = false;
      newItemExpirationDate.value = '';
      newItemSortOrder.value = 0;

      showAddItemModal.value = true;
      error.value = '';
      warningMessage.value = '';
    };

    const closeAddItemModal = () => {
      showAddItemModal.value = false;
      newItemName.value = '';
      newItemType.value = '';
      newItemPrice.value = 0;
      newItemCurrency.value = '';
      newItemMaxPurchases.value = null;
      newItemIsRecommended.value = false;
      newItemExpirationDate.value = '';
      newItemSortOrder.value = 0;
    };

    const addItemToShop = async () => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอินก่อน';
        return;
      }

      if (
        !newItemName.value.trim() ||
        !newItemType.value ||
        newItemPrice.value <= 0 ||
        !newItemCurrency.value
      ) {
        error.value = 'กรุณากรอกชื่อ ประเภทไอเทม ราคา และเลือกประเภทเงินที่ชำระให้ถูกต้อง';
        return;
      }

      const maxPurchases = newItemMaxPurchases.value !== null && newItemMaxPurchases.value > 0
        ? newItemMaxPurchases.value
        : null;

      isAdding.value = true;

      try {
        const itemData = gameItems[newItemName.value.trim()];

        if (!itemData) {
          error.value = 'ไม่พบไอเทมนี้ใน gameItems';
          isAdding.value = false;
          return;
        }

        await projectFirestore.collection('gameShopItems').add({
          name: newItemName.value.trim(),
          type: newItemType.value,
          price: newItemPrice.value,
          currencyType: newItemCurrency.value,
          maxPurchases,
          isRecommended: newItemIsRecommended.value,
          expirationDate: newItemExpirationDate.value
            ? new Date(newItemExpirationDate.value).toISOString()
            : null,
          sortOrder: newItemSortOrder.value || 0,
          createdAt: new Date(),
        });

        warningMessage.value = 'เพิ่มสินค้าสำเร็จ!';
        closeAddItemModal();

        await fetchShopItems();
      } catch (err) {
        console.error('Error adding item to shop:', err);
        error.value = 'เกิดข้อผิดพลาดในการเพิ่มสินค้า';
      } finally {
        isAdding.value = false;
      }
    };

    const openEditItemModal = (item) => {
      if (!item?.id) return;

      editItemId.value = item.id;
      editItemName.value = item.name || '';
      editItemType.value = item.type || '';
      editItemPrice.value = item.price || 0;
      editItemCurrency.value = item.currencyType || '';
      editItemMaxPurchases.value = item.maxPurchases || null;
      editItemIsRecommended.value = item.isRecommended || false;
      editItemExpirationDate.value = item.expirationDate
        ? dayjs(item.expirationDate).format('YYYY-MM-DDTHH:mm')
        : '';
      editItemSortOrder.value = item.sortOrder || 0;

      showEditItemModal.value = true;
      error.value = '';
      warningMessage.value = '';
    };

    const formatDateForInput = (dateString) => {
      if (!dateString) return '';

      const date = dayjs(dateString);

      if (!date.isValid()) return '';

      return date.format('YYYY-MM-DDTHH:mm');
    };

    const closeEditItemModal = () => {
      showEditItemModal.value = false;
      editItemId.value = '';
      editItemName.value = '';
      editItemType.value = '';
      editItemPrice.value = 0;
      editItemCurrency.value = '';
      editItemMaxPurchases.value = null;
      editItemIsRecommended.value = false;
      editItemExpirationDate.value = '';
      editItemSortOrder.value = 0;
    };

    const updateShopItem = async () => {
      if (!user.value || !isAdmin.value || !editItemId.value) {
        error.value = 'คุณไม่มีสิทธิ์แก้ไขสินค้าหรือไอดีไม่ถูกต้อง';
        return;
      }

      if (!editItemType.value || editItemPrice.value <= 0 || !editItemCurrency.value) {
        error.value = 'กรุณากรอกประเภทไอเทม ราคา และเลือกประเภทเงินที่ชำระให้ถูกต้อง';
        return;
      }

      const maxPurchases = editItemMaxPurchases.value !== null && editItemMaxPurchases.value > 0
        ? editItemMaxPurchases.value
        : null;

      isUpdating.value = true;

      try {
        await projectFirestore.collection('gameShopItems').doc(editItemId.value).update({
          type: editItemType.value,
          price: editItemPrice.value,
          currencyType: editItemCurrency.value,
          maxPurchases,
          isRecommended: editItemIsRecommended.value,
          expirationDate: editItemExpirationDate.value
            ? new Date(editItemExpirationDate.value).toISOString()
            : null,
          sortOrder: editItemSortOrder.value || 0,
          updatedAt: new Date(),
        });

        warningMessage.value = 'แก้ไขสินค้าสำเร็จ!';
        closeEditItemModal();

        await fetchShopItems();
      } catch (err) {
        console.error('Error updating shop item:', err);
        error.value = 'เกิดข้อผิดพลาดในการแก้ไขสินค้า';
      } finally {
        isUpdating.value = false;
      }
    };

    const closeModal = () => {
      showModal.value = false;
      selectedShopItem.value = null;
      activeModalTab.value = 'info';
      decomposeItems.value = [];
      modalError.value = '';
      purchaseQuantity.value = 1;
    };

    const decreaseQuantity = () => {
      if (purchaseQuantity.value > 1) {
        purchaseQuantity.value--;
      }
    };

    const increaseQuantity = () => {
      if (purchaseQuantity.value < maxPurchaseQuantity.value) {
        purchaseQuantity.value++;
      }
    };

    onMounted(async () => {
      await fetchShopItems();
    });

    onUnmounted(() => {
      clearShopListeners();
      clearPurchaseListeners();
    });

    return {
      isAdmin,
      activeTab,
      activeModalTab,

      shopItems,
      filteredShopItems,
      expiredShopItems,

      silver,
      currentWeight,
      maxLoad,

      loading,
      error,
      modalError,
      warningMessage,

      showModal,
      showAddItemModal,
      showEditItemModal,

      selectedShopItem,

      isBuying,
      isAdding,
      isUpdating,
      isDeletingExpired,

      newItemName,
      newItemType,
      newItemPrice,
      newItemCurrency,
      newItemMaxPurchases,
      newItemIsRecommended,
      newItemExpirationDate,
      newItemSortOrder,

      editItemId,
      editItemName,
      editItemType,
      editItemPrice,
      editItemCurrency,
      editItemMaxPurchases,
      editItemIsRecommended,
      editItemExpirationDate,
      editItemSortOrder,

      currencyFilter,
      typeFilter,

      userShopData,

      decomposeItems,
      decomposeLoading,

      purchaseQuantity,
      maxPurchaseQuantity,

      getRemainingPurchases,
      decreaseQuantity,
      increaseQuantity,

      tabs,
      formatDate,
      currencyDisplay,
      isExpired,
      getPurchaseInfo,
      hasStats,
      handleImageError,

      selectShopItem,
      buyItem,
      deleteShopItem,
      deleteExpiredShopItems,

      openAddItemModal,
      closeAddItemModal,
      addItemToShop,

      openEditItemModal,
      closeEditItemModal,
      updateShopItem,

      closeModal,
      formatDateForInput,
    };
  },
};
</script>

<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fshop2.png?alt=media&token=e61933b6-77e0-4fab-9338-d71c4cbe686e') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}

/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

/* Animations */
@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}

@keyframes tabSlide {
  from { width: 0; }
  to { width: 100%; }
}
.animate-tabSlide {
  animation: tabSlide 0.2s ease-out;
}

@keyframes spin-pulse {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.animate-spin-pulse {
  animation: spin-pulse 1s infinite linear;
}

/* Modal Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 639px) {
  .text-sm {
    font-size: 0.75rem;
  }
  .text-base {
    font-size: 0.875rem;
  }
  .p-4 {
    padding: 0.75rem;
  }
  .max-h-40 {
    max-height: 10rem;
  }
}
</style>
